import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { MessageCircle, X, Send, User, MapPin, Edit, Check, ArrowLeft, Camera, Users, TrendingUp, Calendar, Mail, UserCheck, Award, Trophy, Star, Eye, Lock, Unlock, Trash2, Bookmark, BookmarkCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/App';
import { processImageForUpload, isValidImageType, formatFileSize, ACCEPTED_IMAGE_TYPES } from '@/utils/imageUtils';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const BADGE_INFO = {
  facility_ready: {
    name: 'Facility Ready',
    description: 'Completed facility setup and qualified for adoption',
    icon: '🏠',
    iconImage: 'https://customer-assets.emergentagent.com/job_ranch-builder/artifacts/x8y8ewep_stall_crossbuck_transparent.png'
  },
  first_adoption: {
    name: 'First Adoption',
    description: 'Adopted your first mustang',
    icon: '🐴',
    iconImage: 'https://customer-assets.emergentagent.com/job_ranch-builder/artifacts/4d3e4ftm_DD4F7E27-71CB-4C69-8CCA-33B7BE07AB10.png'
  },
  mustang_scholar: {
    name: 'Mustang Scholar',
    description: 'Completed multiple educational quizzes',
    icon: '📚',
    iconImage: 'https://customer-assets.emergentagent.com/job_ranch-builder/artifacts/u5b23cdu_A2D0AAE8-96F1-489A-9AA7-B3B935A5145A.png'
  },
  trainer_level_1: {
    name: 'Trainer Level 1',
    description: 'Successfully trained a mustang to halter broke',
    icon: '🎓',
    iconImage: 'https://customer-assets.emergentagent.com/job_ranch-builder/artifacts/v4rquq9j_8311A275-DFFD-438D-B749-75FCB3DB4120.png'
  },
  community_member: {
    name: 'Community Member',
    description: 'Joined the mustang community',
    icon: '👥',
    iconImage: 'https://customer-assets.emergentagent.com/job_ranch-builder/artifacts/ous2ts5j_6AC9B5FC-D823-42F3-80C0-4893845B0B6D.png'
  },
  social_butterfly: {
    name: 'Social Butterfly',
    description: 'Started conversations with 5 community members',
    icon: '💬'
  }
};

const Community = ({ user, logout }) => {
  const navigate = useNavigate();
  const [myProfile, setMyProfile] = useState(null);
  const [communityUsers, setCommunityUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [progress, setProgress] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    // Load sound preference from localStorage
    const saved = localStorage.getItem('communityMessageSound');
    return saved ? JSON.parse(saved) : true; // Default to enabled
  });
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const [userStats, setUserStats] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  
  // View Herd state
  const [showHerdDialog, setShowHerdDialog] = useState(false);
  const [viewingHerd, setViewingHerd] = useState(null);
  const [herdLoading, setHerdLoading] = useState(false);
  const [herdPublic, setHerdPublic] = useState(true);
  
  // Saved conversations state
  const [savedConversations, setSavedConversations] = useState([]);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    state: '',
    bio: '',
    photo_url: ''
  });

  // Create notification sound using Web Audio API
  useEffect(() => {
    // Create a simple notification sound
    const createNotificationSound = () => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    };
    
    audioRef.current = createNotificationSound;
  }, []);

  // Monitor unread count and play sound when it increases
  useEffect(() => {
    const currentUnreadCount = conversations.reduce((acc, conv) => acc + conv.unread_count, 0);
    
    if (currentUnreadCount > previousUnreadCount && previousUnreadCount > 0 && soundEnabled) {
      // New message received, play sound
      try {
        if (audioRef.current) {
          audioRef.current();
        }
      } catch (error) {
        console.error('Error playing notification sound:', error);
      }
    }
    
    setPreviousUnreadCount(currentUnreadCount);
  }, [conversations, soundEnabled]);

  // Save sound preference to localStorage
  useEffect(() => {
    localStorage.setItem('communityMessageSound', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    fetchMyProfile();
    fetchConversations();
    fetchSavedConversations();
    fetchProgress();
    fetchUserStats();
    fetchHerdPrivacy();
  }, []);

  useEffect(() => {
    if (myProfile) {
      fetchCommunityUsers();
    }
  }, [myProfile]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  // Auto-refresh conversations and messages every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (myProfile) {
        fetchConversations();
        if (selectedUser) {
          fetchConversation(selectedUser.user_id);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [myProfile, selectedUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMyProfile = async () => {
    try {
      const response = await api.get('/community/profile');
      setMyProfile(response.data);
      if (response.data) {
        setProfileForm(response.data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunityUsers = async () => {
    try {
      const response = await api.get('/community/users');
      setCommunityUsers(response.data);
    } catch (error) {
      console.error('Error fetching community users:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/community/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchSavedConversations = async () => {
    try {
      const response = await api.get('/community/conversations/saved');
      setSavedConversations(response.data);
    } catch (error) {
      console.error('Error fetching saved conversations:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await api.get('/user/progress');
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };


  const fetchUserStats = async () => {
    try {
      const response = await api.get('/user/stats');
      setUserStats(response.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };


  const fetchConversation = async (userId) => {
    try {
      const response = await api.get(`/community/messages/${userId}`);
      setConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  const handleDeleteConversation = async (userId, userName) => {
    console.log('[DELETE] handleDeleteConversation called with:', userId, userName);
    
    // Use toast action for confirmation on mobile
    toast(
      `Delete conversation with ${userName}?`,
      {
        description: 'This action cannot be undone.',
        action: {
          label: 'Delete',
          onClick: async () => {
            try {
              console.log('[DELETE] Sending delete request for:', userId);
              const response = await api.delete(`/community/conversations/${userId}`);
              console.log('[DELETE] Response:', response.data);
              toast.success(`Conversation deleted (${response.data.deleted_count} messages removed)`);
              setShowMessageDialog(false);
              setSelectedUser(null);
              setConversation([]);
              await fetchConversations();
              await fetchSavedConversations();
            } catch (error) {
              console.error('Error deleting conversation:', error);
              toast.error(error.response?.data?.detail || 'Failed to delete conversation');
            }
          }
        },
        cancel: {
          label: 'Cancel',
          onClick: () => {}
        },
        duration: 10000,
      }
    );
  };

  const handleSaveConversation = async (userId) => {
    try {
      await api.post(`/community/conversations/${userId}/save`);
      toast.success('Conversation saved!');
      await fetchConversations();
      await fetchSavedConversations();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Conversation already saved');
      } else {
        console.error('Error saving conversation:', error);
        toast.error('Failed to save conversation');
      }
    }
  };

  const handleUnsaveConversation = async (userId) => {
    try {
      await api.delete(`/community/conversations/${userId}/save`);
      toast.success('Conversation removed from saved');
      await fetchConversations();
      await fetchSavedConversations();
    } catch (error) {
      console.error('Error unsaving conversation:', error);
      toast.error('Failed to unsave conversation');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (including HEIC/HEIF)
    if (!isValidImageType(file)) {
      toast.error('Please select an image file (JPG, PNG, GIF, WEBP, or HEIC)');
      return;
    }

    try {
      // Process image (convert HEIC if needed, compress if over 8MB)
      toast.info('Processing image...');
      const processedFile = await processImageForUpload(file, 8);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileForm({ ...profileForm, photo_url: reader.result });
        toast.success(`Image processed! (${formatFileSize(processedFile.size)})`);
      };
      reader.readAsDataURL(processedFile);
    } catch (error) {
      console.error('Image processing error:', error);
      toast.error(error.message || 'Failed to process image');
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.first_name || !profileForm.state || !profileForm.bio) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Simple sentence validation - just check length
    const bioLength = profileForm.bio.trim().length;
    if (bioLength < 10) {
      toast.error('Bio is too short (minimum 10 characters)');
      return;
    }
    if (bioLength > 500) {
      toast.error('Bio is too long (maximum 500 characters)');
      return;
    }

    try {
      const response = await api.post('/community/profile', profileForm);
      setMyProfile(response.data);
      setShowProfileDialog(false);
      toast.success('Profile saved successfully!');
      fetchCommunityUsers();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const handleStartConversation = async (communityUser) => {
    setSelectedUser(communityUser);
    setShowMessageDialog(true);
    await fetchConversation(communityUser.user_id);
  };

  // View another user's herd
  const handleViewHerd = async (communityUser) => {
    setHerdLoading(true);
    setShowHerdDialog(true);
    try {
      const response = await api.get(`/users/${communityUser.user_id}/herd`);
      setViewingHerd(response.data);
    } catch (error) {
      console.error('Error fetching herd:', error);
      if (error.response?.status === 403) {
        toast.error("This user's herd is private");
      } else {
        toast.error('Failed to load herd');
      }
      setShowHerdDialog(false);
    } finally {
      setHerdLoading(false);
    }
  };

  // Fetch current user's herd privacy setting
  const fetchHerdPrivacy = async () => {
    try {
      const response = await api.get('/users/me/herd-visibility');
      setHerdPublic(response.data.is_public);
    } catch (error) {
      console.error('Error fetching herd privacy:', error);
    }
  };

  // Toggle herd privacy
  const toggleHerdPrivacy = async () => {
    try {
      const newValue = !herdPublic;
      await api.put('/users/me/herd-visibility', { is_public: newValue });
      setHerdPublic(newValue);
      toast.success(newValue ? 'Your herd is now public' : 'Your herd is now private');
    } catch (error) {
      console.error('Error updating herd privacy:', error);
      toast.error('Failed to update privacy setting');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    setSendingMessage(true);
    try {
      await api.post('/community/messages', {
        receiver_id: selectedUser.user_id,
        message: newMessage
      });
      setNewMessage('');
      await fetchConversation(selectedUser.user_id);
      await fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
        <Navigation user={user} logout={logout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: '#372F2F' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!myProfile) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
        <Navigation user={user} logout={logout} />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="p-8 text-center" style={{ border: '2px solid #6FCFD3' }}>
            <User className="w-20 h-20 mx-auto mb-4" style={{ color: '#D66238' }} />
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#D66238' }}>
              Welcome to the Community!
            </h2>
            <p className="text-lg mb-6" style={{ color: '#372F2F' }}>
              Create your profile to connect with other mustang enthusiasts in your state.
            </p>
            <Button
              onClick={() => setShowProfileDialog(true)}
              className="text-lg px-8 py-6"
              style={{ backgroundColor: '#6FCFD3', color: '#fff' }}
            >
              Create Profile
            </Button>
          </Card>
        </div>

        <ProfileDialog
          open={showProfileDialog}
          onOpenChange={setShowProfileDialog}
          profileForm={profileForm}
          setProfileForm={setProfileForm}
          handleSaveProfile={handleSaveProfile}
          handleFileChange={handleFileChange}
          fileInputRef={fileInputRef}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* MY STATS SECTION - Compact */}
        <Card className="mb-6 p-4" style={{ border: '2px solid #6FCFD3' }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Profile Mini */}
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12 border-2" style={{ borderColor: '#6FCFD3' }}>
                {myProfile.photo_url ? (
                  <AvatarImage src={myProfile.photo_url} alt={myProfile.first_name} />
                ) : (
                  <AvatarFallback style={{ backgroundColor: '#6FCFD3', color: '#fff' }}>
                    {myProfile.first_name[0].toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <h3 className="font-bold" style={{ color: '#D66238' }}>{myProfile.first_name}</h3>
                <p className="text-xs flex items-center gap-1" style={{ color: '#6FCFD3' }}>
                  <MapPin className="w-3 h-3" />
                  {myProfile.state}
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            {userStats && (
              <div className="flex flex-wrap gap-4 text-center">
                <div>
                  <div className="text-lg font-bold" style={{ color: '#D66238' }}>{userStats.coin_balance} 🪙</div>
                  <div className="text-xs" style={{ color: '#372F2F' }}>Coins</div>
                </div>
                <div>
                  <div className="text-lg font-bold" style={{ color: '#6FCFD3' }}>{userStats.horses_adopted}</div>
                  <div className="text-xs" style={{ color: '#372F2F' }}>Horses</div>
                </div>
                <div>
                  <div className="text-lg font-bold" style={{ color: '#D66238' }}>{userStats.quizzes_completed}</div>
                  <div className="text-xs" style={{ color: '#372F2F' }}>Quizzes</div>
                </div>
                <div>
                  <div className="text-lg font-bold" style={{ color: '#6FCFD3' }}>{progress?.level || 1}</div>
                  <div className="text-xs" style={{ color: '#372F2F' }}>Level</div>
                </div>
                <div>
                  <div className="text-lg font-bold" style={{ color: '#D66238' }}>{conversations.length}</div>
                  <div className="text-xs" style={{ color: '#372F2F' }}>Chats</div>
                </div>
              </div>
            )}
            
            {/* Edit Profile Button */}
            <Button
              onClick={() => setShowProfileDialog(true)}
              variant="outline"
              size="sm"
              className="gap-1"
              style={{ borderColor: '#6FCFD3', color: '#6FCFD3' }}
            >
              <Edit className="w-3 h-3" />
              Edit
            </Button>
          </div>
        </Card>

        {/* JOIN THE COMMUNITY SECTION */}
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold" style={{ color: '#D66238' }}>
              Community
            </h2>
          </div>

          <Tabs defaultValue="directory" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8" style={{ backgroundColor: '#fff', border: '2px solid #6FCFD3' }}>
            <TabsTrigger value="directory" style={{ color: '#372F2F' }}>
              Directory
            </TabsTrigger>
            <TabsTrigger value="messages" style={{ color: '#372F2F' }}>
              Messages {conversations.length > 0 && `(${conversations.length})`}
            </TabsTrigger>
            <TabsTrigger value="saved" style={{ color: '#372F2F' }} data-testid="saved-conversations-tab">
              Saved {savedConversations.length > 0 && `(${savedConversations.length})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="directory">
            {Object.keys(communityUsers).length === 0 ? (
              <Card className="p-12 text-center">
                <Users className="w-20 h-20 mx-auto mb-4" style={{ color: '#6FCFD3' }} />
                <p className="text-xl" style={{ color: '#372F2F' }}>
                  No community members yet. Check back soon!
                </p>
              </Card>
            ) : (
              <div className="space-y-8">
                {Object.entries(communityUsers).sort(([a], [b]) => a.localeCompare(b)).map(([state, users]) => (
                  <div key={state}>
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#D66238' }}>
                      <MapPin className="w-6 h-6" />
                      {state}
                      <Badge variant="secondary" className="ml-2">
                        {users.length} {users.length === 1 ? 'member' : 'members'}
                      </Badge>
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {users.map((communityUser) => (
                        <Card
                          key={communityUser.user_id}
                          className="p-6 hover:shadow-lg transition-shadow"
                          style={{ border: '2px solid #6FCFD3' }}
                        >
                          <div className="flex items-start gap-4 mb-4">
                            <Avatar className="w-16 h-16 border-2" style={{ borderColor: '#6FCFD3' }}>
                              {communityUser.photo_url ? (
                                <AvatarImage src={communityUser.photo_url} alt={communityUser.first_name} />
                              ) : (
                                <AvatarFallback style={{ backgroundColor: '#6FCFD3', color: '#fff', fontSize: '1.5rem' }}>
                                  {communityUser.first_name[0].toUpperCase()}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold" style={{ color: '#D66238' }}>
                                {communityUser.first_name}
                              </h3>
                              <p className="text-sm flex items-center gap-1" style={{ color: '#6FCFD3' }}>
                                <MapPin className="w-3 h-3" />
                                {communityUser.state}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm mb-4 line-clamp-3" style={{ color: '#372F2F' }}>
                            {communityUser.bio}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleViewHerd(communityUser)}
                              className="flex-1 gap-2"
                              variant="outline"
                              style={{ borderColor: '#D66238', color: '#D66238' }}
                            >
                              <Eye className="w-4 h-4" />
                              View Herd
                            </Button>
                            <Button
                              onClick={() => handleStartConversation(communityUser)}
                              className="flex-1 gap-2"
                              style={{ backgroundColor: '#6FCFD3', color: '#fff' }}
                            >
                              <MessageCircle className="w-4 h-4" />
                              Message
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages">
            {conversations.length === 0 ? (
              <Card className="p-12 text-center">
                <MessageCircle className="w-20 h-20 mx-auto mb-4" style={{ color: '#6FCFD3' }} />
                <p className="text-xl" style={{ color: '#372F2F' }}>
                  No conversations yet. Start a conversation from the Community Directory!
                </p>
              </Card>
            ) : (
              <>
                {/* Unread Messages Section */}
                {conversations.filter(conv => conv.unread_count > 0).length > 0 && (
                  <Card className="p-6 mb-6" style={{ backgroundColor: '#FFF3CD', border: '2px solid #D66238' }}>
                    <div className="flex items-center gap-2 mb-4">
                      <Mail className="w-5 h-5" style={{ color: '#D66238' }} />
                      <h3 className="text-xl font-bold" style={{ color: '#D66238' }}>
                        Unread Messages ({conversations.reduce((acc, conv) => acc + conv.unread_count, 0)})
                      </h3>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {conversations
                        .filter(conv => conv.unread_count > 0)
                        .map((conv) => (
                          <Card
                            key={conv.user_id}
                            className="p-4 hover:shadow-lg transition-all cursor-pointer animate-pulse"
                            style={{ border: '2px solid #D66238', backgroundColor: '#fff' }}
                            onClick={() => {
                              setSelectedUser(conv);
                              setShowMessageDialog(true);
                              fetchConversation(conv.user_id);
                            }}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <Avatar className="w-10 h-10 border-2" style={{ borderColor: '#D66238' }}>
                                {conv.photo_url ? (
                                  <AvatarImage src={conv.photo_url} alt={conv.first_name} />
                                ) : (
                                  <AvatarFallback style={{ backgroundColor: '#D66238', color: '#fff' }}>
                                    {conv.first_name[0].toUpperCase()}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <div className="flex-1">
                                <h3 className="font-bold text-sm" style={{ color: '#D66238' }}>
                                  {conv.first_name}
                                </h3>
                                <Badge style={{ backgroundColor: '#D66238', color: '#fff', fontSize: '0.7rem' }}>
                                  {conv.unread_count} new message{conv.unread_count > 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-xs line-clamp-1" style={{ color: '#372F2F', opacity: 0.7 }}>
                              {conv.last_message}
                            </p>
                          </Card>
                        ))}
                    </div>
                  </Card>
                )}

                {/* All Conversations */}
                <h3 className="text-xl font-bold mb-4" style={{ color: '#D66238' }}>
                  All Conversations
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {conversations.map((conv) => (
                  <Card
                    key={conv.user_id}
                    className="p-6 hover:shadow-lg transition-shadow relative"
                    style={{ border: conv.is_saved ? '2px solid #D66238' : '2px solid #6FCFD3' }}
                  >
                    {/* Saved indicator */}
                    {conv.is_saved && (
                      <div className="absolute top-2 right-2">
                        <BookmarkCheck className="w-4 h-4" style={{ color: '#D66238' }} />
                      </div>
                    )}
                    <div 
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedUser(conv);
                        setShowMessageDialog(true);
                        fetchConversation(conv.user_id);
                      }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Avatar className="w-12 h-12 border-2" style={{ borderColor: '#6FCFD3' }}>
                          {conv.photo_url ? (
                            <AvatarImage src={conv.photo_url} alt={conv.first_name} />
                          ) : (
                            <AvatarFallback style={{ backgroundColor: '#6FCFD3', color: '#fff' }}>
                              {conv.first_name[0].toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-bold" style={{ color: '#D66238' }}>
                            {conv.first_name}
                          </h3>
                          {conv.unread_count > 0 && (
                            <Badge style={{ backgroundColor: '#D66238', color: '#fff' }}>
                              {conv.unread_count} new
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm line-clamp-2 mb-3" style={{ color: '#372F2F', opacity: 0.7 }}>
                        {conv.is_sender ? 'You: ' : ''}{conv.last_message}
                      </p>
                    </div>
                    {/* Action buttons */}
                    <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid #e0e0e0' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 gap-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          conv.is_saved 
                            ? handleUnsaveConversation(conv.user_id)
                            : handleSaveConversation(conv.user_id);
                        }}
                        style={{ color: conv.is_saved ? '#D66238' : '#6FCFD3' }}
                        data-testid={`save-conv-${conv.user_id}`}
                      >
                        {conv.is_saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                        {conv.is_saved ? 'Saved' : 'Save'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 gap-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('[DELETE CLICK] Button clicked for:', conv.user_id, conv.first_name);
                          handleDeleteConversation(conv.user_id, conv.first_name);
                        }}
                        style={{ color: '#D66238' }}
                        data-testid={`delete-conv-${conv.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    </div>
                  </Card>
                ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* Saved Conversations Tab */}
          <TabsContent value="saved" data-testid="saved-conversations-content">
            {savedConversations.length === 0 ? (
              <Card className="p-12 text-center">
                <Bookmark className="w-20 h-20 mx-auto mb-4" style={{ color: '#6FCFD3' }} />
                <p className="text-xl mb-2" style={{ color: '#372F2F' }}>
                  No saved conversations yet
                </p>
                <p className="text-sm" style={{ color: '#372F2F', opacity: 0.7 }}>
                  Save important conversations to quickly access them later. Click the bookmark icon on any conversation to save it.
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold" style={{ color: '#D66238' }}>
                    Saved Conversations ({savedConversations.length})
                  </h3>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {savedConversations.map((saved) => (
                    <Card
                      key={saved.other_user_id}
                      className="p-6 hover:shadow-lg transition-shadow relative"
                      style={{ border: '2px solid #D66238' }}
                      data-testid={`saved-conv-card-${saved.other_user_id}`}
                    >
                      {/* Saved indicator */}
                      <div className="absolute top-2 right-2">
                        <BookmarkCheck className="w-5 h-5" style={{ color: '#D66238' }} />
                      </div>
                      <div 
                        className="cursor-pointer"
                        onClick={() => {
                          setSelectedUser({
                            user_id: saved.other_user_id,
                            first_name: saved.other_user_name,
                            photo_url: saved.other_user_photo,
                            is_saved: true
                          });
                          setShowMessageDialog(true);
                          fetchConversation(saved.other_user_id);
                        }}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="w-12 h-12 border-2" style={{ borderColor: '#D66238' }}>
                            {saved.other_user_photo ? (
                              <AvatarImage src={saved.other_user_photo} alt={saved.other_user_name} />
                            ) : (
                              <AvatarFallback style={{ backgroundColor: '#D66238', color: '#fff' }}>
                                {saved.other_user_name[0]?.toUpperCase() || '?'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="font-bold" style={{ color: '#D66238' }}>
                              {saved.other_user_name}
                            </h3>
                            {saved.unread_count > 0 && (
                              <Badge style={{ backgroundColor: '#D66238', color: '#fff' }}>
                                {saved.unread_count} new
                              </Badge>
                            )}
                          </div>
                        </div>
                        {saved.last_message && (
                          <p className="text-sm line-clamp-2 mb-3" style={{ color: '#372F2F', opacity: 0.7 }}>
                            {saved.last_message}
                          </p>
                        )}
                        <p className="text-xs" style={{ color: '#6FCFD3' }}>
                          Saved {new Date(saved.saved_at).toLocaleDateString()}
                        </p>
                      </div>
                      {/* Action buttons */}
                      <div className="flex gap-2 pt-3 mt-3" style={{ borderTop: '1px solid #e0e0e0' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1 gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnsaveConversation(saved.other_user_id);
                          }}
                          style={{ color: '#D66238' }}
                          data-testid={`unsave-conv-${saved.other_user_id}`}
                        >
                          <BookmarkCheck className="w-4 h-4" />
                          Unsave
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1 gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConversation(saved.other_user_id, saved.other_user_name);
                          }}
                          style={{ color: '#D66238' }}
                          data-testid={`delete-saved-conv-${saved.other_user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Profile Dialog */}
      <ProfileDialog
        open={showProfileDialog}
        onOpenChange={setShowProfileDialog}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        handleSaveProfile={handleSaveProfile}
        handleFileChange={handleFileChange}
        fileInputRef={fileInputRef}
        herdPublic={herdPublic}
        toggleHerdPrivacy={toggleHerdPrivacy}
      />

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog} modal={false}>
        <DialogContent className="max-w-2xl max-h-[80vh] p-0" style={{ backgroundColor: '#FCFCF8' }}>
          {selectedUser && (
            <>
              {/* Header */}
              <DialogHeader className="p-6 pb-4" style={{ borderBottom: '2px solid #6FCFD3' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12 border-2" style={{ borderColor: '#6FCFD3' }}>
                      {selectedUser.photo_url ? (
                        <AvatarImage src={selectedUser.photo_url} alt={selectedUser.first_name} />
                      ) : (
                        <AvatarFallback style={{ backgroundColor: '#6FCFD3', color: '#fff' }}>
                          {selectedUser.first_name[0].toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div>
                      <DialogTitle style={{ color: '#D66238' }}>{selectedUser.first_name}</DialogTitle>
                      {selectedUser.state && (
                        <p className="text-sm flex items-center gap-1" style={{ color: '#6FCFD3' }}>
                          <MapPin className="w-3 h-3" />
                          {selectedUser.state}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Save/Unsave Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => selectedUser.is_saved 
                        ? handleUnsaveConversation(selectedUser.user_id)
                        : handleSaveConversation(selectedUser.user_id)
                      }
                      title={selectedUser.is_saved ? "Remove from saved" : "Save conversation"}
                      data-testid="save-conversation-btn"
                    >
                      {selectedUser.is_saved ? (
                        <BookmarkCheck className="w-5 h-5" style={{ color: '#6FCFD3' }} />
                      ) : (
                        <Bookmark className="w-5 h-5" style={{ color: '#6FCFD3' }} />
                      )}
                    </Button>
                    {/* Delete Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        console.log('[DELETE CLICK DIALOG] Button clicked for:', selectedUser.user_id, selectedUser.first_name);
                        handleDeleteConversation(selectedUser.user_id, selectedUser.first_name);
                      }}
                      title="Delete conversation"
                      data-testid="delete-conversation-btn"
                    >
                      <Trash2 className="w-5 h-5" style={{ color: '#D66238' }} />
                    </Button>
                    {/* Close Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowMessageDialog(false)}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4" style={{ maxHeight: '400px' }}>
                {conversation.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#6FCFD3', opacity: 0.5 }} />
                    <p style={{ color: '#372F2F', opacity: 0.7 }}>
                      No messages yet. Start the conversation!
                    </p>
                  </div>
                ) : (
                  conversation.map((msg, idx) => {
                    const isMyMessage = msg.sender_id === user.id;
                    return (
                      <div
                        key={idx}
                        className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            isMyMessage ? 'rounded-tr-none' : 'rounded-tl-none'
                          }`}
                          style={{
                            backgroundColor: isMyMessage ? '#6FCFD3' : '#fff',
                            color: isMyMessage ? '#fff' : '#372F2F',
                            border: isMyMessage ? 'none' : '2px solid #6FCFD3'
                          }}
                        >
                          <p className="break-words">{msg.message}</p>
                          <p
                            className="text-xs mt-1"
                            style={{ opacity: 0.7 }}
                          >
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-6 pt-4" style={{ borderTop: '2px solid #6FCFD3' }}>
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1"
                    style={{ borderColor: '#6FCFD3' }}
                    disabled={sendingMessage}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    style={{ backgroundColor: '#6FCFD3', color: '#fff' }}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Herd Dialog */}
      <Dialog open={showHerdDialog} onOpenChange={setShowHerdDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ color: '#D66238' }}>
              {viewingHerd?.user?.picture && (
                <Avatar className="w-10 h-10">
                  <AvatarImage src={viewingHerd.user.picture} alt={viewingHerd?.user?.name} />
                  <AvatarFallback style={{ backgroundColor: '#6FCFD3', color: '#fff' }}>
                    {viewingHerd?.user?.name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              )}
              {viewingHerd?.user?.name} Herd
            </DialogTitle>
          </DialogHeader>
          
          {herdLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#6FCFD3' }}></div>
            </div>
          ) : viewingHerd?.horses?.length === 0 ? (
            <div className="text-center py-12">
              <p style={{ color: '#372F2F', opacity: 0.7 }}>
                This user has not added any horses to their herd yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {viewingHerd?.horses?.map((horse) => (
                <Card key={horse.id} className="overflow-hidden" style={{ border: '2px solid #6FCFD3' }}>
                  {horse.photo_url && (
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src={horse.photo_url?.startsWith('/api/media/') && !horse.photo_url.includes('/thumb/')
                          ? `${process.env.REACT_APP_BACKEND_URL}${horse.photo_url.replace('/api/media/', '/api/media/thumb/')}`
                          : horse.photo_url?.startsWith('/api/')
                          ? `${process.env.REACT_APP_BACKEND_URL}${horse.photo_url}` 
                          : horse.photo_url
                        }
                        alt={horse.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          if (e.target.src.includes('/thumb/') && horse.photo_url) {
                            e.target.src = horse.photo_url.startsWith('/api/')
                              ? `${process.env.REACT_APP_BACKEND_URL}${horse.photo_url}`
                              : horse.photo_url;
                          }
                        }}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-lg font-bold mb-2" style={{ color: '#D66238' }}>
                      {horse.name}
                      {horse.is_real_adoption && (
                        <span className="ml-2 text-yellow-500" title="Real Adoption">⭐</span>
                      )}
                    </h3>
                    <div className="space-y-1 text-sm" style={{ color: '#372F2F' }}>
                      {horse.tag_number && (
                        <p><span className="font-semibold">Tag:</span> {horse.tag_number}</p>
                      )}
                      <p><span className="font-semibold">Age:</span> {horse.age} years</p>
                      <p><span className="font-semibold">Gender:</span> {horse.gender}</p>
                      <p><span className="font-semibold">Color:</span> {horse.color}</p>
                      {horse.hma && (
                        <p><span className="font-semibold">HMA:</span> {horse.hma}</p>
                      )}
                      {horse.height && (
                        <p><span className="font-semibold">Height:</span> {horse.height}</p>
                      )}
                      {horse.disposition && (
                        <p><span className="font-semibold">Disposition:</span> {horse.disposition}</p>
                      )}
                    </div>
                    {/* Brand Symbols */}
                    {horse.brand_symbols && horse.brand_symbols.length > 0 && (
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: '#e0e0e0' }}>
                        <p className="text-xs font-semibold mb-2" style={{ color: '#372F2F' }}>Brand Symbols:</p>
                        <div className="flex flex-wrap gap-1">
                          {horse.brand_symbols.slice(0, 8).map((symbol, idx) => (
                            <img 
                              key={idx} 
                              src={symbol} 
                              alt={`Brand ${idx + 1}`}
                              className="w-6 h-6 object-contain"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setShowHerdDialog(false)}
              style={{ backgroundColor: '#6FCFD3', color: '#fff' }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  return (
    <Card className="p-6 text-center" style={{ border: '2px solid #6FCFD3' }}>
      <div className="flex justify-center mb-3" style={{ color: color }}>
        {icon}
      </div>
      <div className="text-3xl font-bold mb-2" style={{ color: '#D66238' }}>
        {value}
      </div>
      <div className="text-sm font-semibold" style={{ color: '#372F2F' }}>
        {label}
      </div>
    </Card>
  );
};

// Profile Dialog Component
const ProfileDialog = ({ open, onOpenChange, profileForm, setProfileForm, handleSaveProfile, handleFileChange, fileInputRef, herdPublic, toggleHerdPrivacy }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="max-w-2xl" style={{ backgroundColor: '#FCFCF8' }}>
        <DialogHeader>
          <DialogTitle style={{ color: '#D66238', fontSize: '1.5rem' }}>
            {profileForm.first_name ? 'Edit Profile' : 'Create Profile'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Photo Upload */}
          <div className="flex flex-col items-center gap-4">
            <Avatar className="w-32 h-32 border-4" style={{ borderColor: '#6FCFD3' }}>
              {profileForm.photo_url ? (
                <AvatarImage src={profileForm.photo_url} alt="Profile" className="object-cover" />
              ) : (
                <AvatarFallback style={{ backgroundColor: '#6FCFD3', color: '#fff', fontSize: '3rem' }}>
                  {profileForm.first_name ? profileForm.first_name[0].toUpperCase() : <Camera />}
                </AvatarFallback>
              )}
            </Avatar>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="gap-2"
              style={{ borderColor: '#6FCFD3', color: '#6FCFD3' }}
            >
              <Camera className="w-4 h-4" />
              {profileForm.photo_url ? 'Change Photo' : 'Upload Photo'}
            </Button>
            <p className="text-xs text-center" style={{ color: '#372F2F', opacity: 0.7 }}>
              Supports iPhone photos (HEIC). Auto-compresses to 8MB.
            </p>
          </div>

          {/* First Name */}
          <div>
            <Label htmlFor="first_name" style={{ color: '#372F2F' }}>
              First Name <span style={{ color: '#D66238' }}>*</span>
            </Label>
            <Input
              id="first_name"
              value={profileForm.first_name}
              onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
              placeholder="Enter your first name"
              style={{ borderColor: '#6FCFD3' }}
            />
          </div>

          {/* State */}
          <div>
            <Label htmlFor="state" style={{ color: '#372F2F' }}>
              State <span style={{ color: '#D66238' }}>*</span>
            </Label>
            <Select
              value={profileForm.state}
              onValueChange={(value) => setProfileForm({ ...profileForm, state: value })}
            >
              <SelectTrigger style={{ borderColor: '#6FCFD3' }}>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {US_STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bio */}
          <div>
            <Label htmlFor="bio" style={{ color: '#372F2F' }}>
              Bio (1-4 sentences) <span style={{ color: '#D66238' }}>*</span>
            </Label>
            <Textarea
              id="bio"
              value={profileForm.bio}
              onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
              placeholder="Tell others about your interest in mustangs..."
              rows={4}
              style={{ borderColor: '#6FCFD3' }}
            />
            <p className="text-xs mt-1" style={{ color: '#372F2F', opacity: 0.7 }}>
              Share your experience with horses, why you love mustangs, or what you are looking for in the community.
            </p>
          </div>

          {/* Herd Privacy Toggle */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold" style={{ color: '#372F2F' }}>
                  Herd Visibility
                </Label>
                <p className="text-xs mt-1" style={{ color: '#372F2F', opacity: 0.7 }}>
                  {herdPublic 
                    ? 'Your herd is visible to other community members' 
                    : 'Your herd is hidden from other community members'}
                </p>
              </div>
              <Button
                onClick={toggleHerdPrivacy}
                variant="outline"
                size="sm"
                className="gap-2"
                style={{ 
                  borderColor: herdPublic ? '#6FCFD3' : '#D66238',
                  color: herdPublic ? '#6FCFD3' : '#D66238'
                }}
              >
                {herdPublic ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                {herdPublic ? 'Public' : 'Private'}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1"
              style={{ borderColor: '#6FCFD3', color: '#6FCFD3' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveProfile}
              className="flex-1 gap-2"
              style={{ backgroundColor: '#6FCFD3', color: '#fff' }}
            >
              <Check className="w-4 h-4" />
              Save Profile
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Community;
